import {assert} from 'Asserts';
import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import {isColumnTypeNumerical} from 'data-frames/DataFrameTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';

import {ChartOptionType} from './ChartingTypes';

const barChartParameters = {
  seriesColumnName: null,
  sortDirection: 'ASCENDING',
  tryConvertStrings: null,
  yAxisMin: null,
  yAxisMax: null,
  yAxisScale: 'LINEAR',
  fillNullsWithDefaultValues: null,
  errorBarColumnName: null,
  discreteColorPalette: 'BENCHLING',
  seriesColorConfigs: [] as Array<{series: string | null; color: string | null}>,
} as const;

export function getDefaultChartConfig(
  chartOptionType: ChartOptionType,
  dataFrameColumns: DataFrameColumns
): PipelineStepConfig<PipelineStepType.CHART_VIEW, 0> {
  const allowedChartTypes = [
    ChartOptionType.BOXPLOT,
    ChartOptionType.PIE,
    ChartOptionType.LINE,
    ChartOptionType.HEATMAP,
    ChartOptionType.SCATTER,
    ChartOptionType.GROUPED_BAR,
    ChartOptionType.STACKED_BAR,
  ];
  assert(
    allowedChartTypes.includes(chartOptionType),
    'Only boxplot, pie, line chart, stacked bar chart, grouped bar chart, scatter plot, and heatmap can be created without a visualization parameter object'
  );

  const firstTextColumnIndex = dataFrameColumns.findIndex((column) => column.type.leafType === LeafType.TEXT);
  const firstNumberColumnIndex = dataFrameColumns.findIndex((column) => isColumnTypeNumerical(column.type));
  const textColumnName =
    firstTextColumnIndex !== -1
      ? dataFrameColumns[firstTextColumnIndex].systemName
      : dataFrameColumns[0].systemName;
  const numberColumnName =
    firstNumberColumnIndex !== -1
      ? dataFrameColumns[firstNumberColumnIndex].systemName
      : dataFrameColumns[0].systemName;

  switch (chartOptionType) {
    case ChartOptionType.PIE:
      return {
        chartConfig: {
          type: 'PIE_CHART' as const,
          parameters: {
            legendLocation: 'RIGHT',
            measureColumnName: numberColumnName,
            sliceColumnName: textColumnName,
            showPercentageInLabel: true,
            showSliceNameInLabel: true,
            showValueInLabel: false,
            tryConvertStrings: null,
            fillNullsWithDefaultValues: null,
            showLegend: true,
            legendLabel: null,
            discreteColorPalette: 'BENCHLING',
            seriesColorConfigs: [],
          },
        },
        chartName: 'Pie chart',
      };
    case ChartOptionType.LINE:
      return {
        chartConfig: {
          type: 'LINE_CHART' as const,
          parameters: {
            xAxisColumnName: textColumnName,
            yAxisColumnName: numberColumnName,
            seriesColumnName: null,
            tryConvertStrings: null,
            fillNullsWithDefaultValues: null,
            xAxisScale: 'LINEAR',
            xAxisMin: null,
            xAxisMax: null,
            yAxisScale: 'LINEAR',
            yAxisMin: null,
            yAxisMax: null,
            yAxisIncludesZero: false,
            errorBarColumnName: null,
            showLegend: null,
            legendLabel: null,
            legendLocation: null,
            markerShape: null,
            markerOpacity: null,
            markerShapeOpen: null,
            pointLabelColumnName: null,
            xAxisLabel: null,
            yAxisLabel: null,
            discreteColorPalette: 'BENCHLING',
            seriesColorConfigs: [],
          },
        },
        chartName: 'Line chart',
      };
    case ChartOptionType.HEATMAP: {
      // Use same positional logic as VisualizationTypeSetters: x=0, y=clamp(1), metric=clamp(2)
      const xIndex = 0;
      const yIndex = Math.min(1, dataFrameColumns.length - 1);
      const metricIndex = Math.min(2, dataFrameColumns.length - 1);

      const heatmapXAxisColumnName = dataFrameColumns[xIndex].systemName;
      const heatmapYAxisColumnName = dataFrameColumns[yIndex].systemName;
      const heatmapMetricColumnName = dataFrameColumns[metricIndex].systemName;

      return {
        chartConfig: {
          type: 'HEATMAP' as const,
          parameters: {
            xAxisColumnName: heatmapXAxisColumnName,
            yAxisColumnName: heatmapYAxisColumnName,
            gradientDirection: 'ASCENDING_METRIC_VALUES',
            metricColumnName: heatmapMetricColumnName,
            tryConvertStrings: null,
            fillNullsWithDefaultValues: null,
            showLegend: null,
            legendLabel: null,
            legendLocation: null,
          },
        },
        chartName: 'Heatmap',
      };
    }
    case ChartOptionType.SCATTER:
      return {
        chartConfig: {
          type: 'SCATTER_PLOT' as const,
          parameters: {
            xAxisColumnName: textColumnName,
            yAxisColumnName: numberColumnName,
            colorPalette: 'DISCRETE',
            legendLocation: null,
            seriesColumnName: null,
            shapeSeriesColumnName: null,
            seriesColorConfigs: null,
            seriesShapeConfigs: null,
            tryConvertStrings: null,
            fillNullsWithDefaultValues: null,
            xAxisScale: 'LINEAR',
            xAxisMin: null,
            xAxisMax: null,
            yAxisIncludesZero: false,
            yAxisMin: null,
            yAxisMax: null,
            yAxisScale: 'LINEAR',
            trendlineType: null,
            annotationLineConfigs: [],
            errorBarColumnName: null,
            showLegend: null,
            legendLabel: null,
            markerShape: null,
            markerOpacity: null,
            markerShapeOpen: null,
            pointLabelColumnName: null,
            xAxisLabel: null,
            yAxisLabel: null,
          },
        },
        chartName: 'Scatter plot',
      };
    case ChartOptionType.GROUPED_BAR:
      return {
        chartConfig: {
          type: 'BAR_CHART' as const,
          parameters: {
            ...barChartParameters,
            barChartType: 'STANDARD',
            xAxisColumnName: textColumnName,
            yAxisColumnName: numberColumnName,
            showLegend: null,
            legendLabel: null,
            legendLocation: null,
            xAxisLabel: null,
            yAxisLabel: null,
          },
        },
        chartName: 'Grouped bar chart',
      };
    case ChartOptionType.STACKED_BAR:
      return {
        chartConfig: {
          type: 'BAR_CHART' as const,
          parameters: {
            ...barChartParameters,
            barChartType: 'STACKED',
            xAxisColumnName: textColumnName,
            yAxisColumnName: numberColumnName,
            showLegend: null,
            legendLabel: null,
            legendLocation: null,
            xAxisLabel: null,
            yAxisLabel: null,
          },
        },
        chartName: 'Stacked bar chart',
      };
    case ChartOptionType.BOXPLOT:
      return {
        chartConfig: {
          type: 'BOX_PLOT' as const,
          parameters: {
            xAxisColumnName: textColumnName,
            yAxisColumnName: numberColumnName,
            showPoints: 'ALL',
            showLegend: null,
            xAxisLabel: null,
            xAxisMin: null,
            xAxisMax: null,
            sortDirection: 'ASCENDING',
            legendLocation: 'RIGHT',
            pointLabelColumnName: null,
            yAxisLabel: null,
            yAxisMin: null,
            yAxisMax: null,
            invertYAxis: false,
            seriesColumnName: null,
            legendLabel: null,
            markerShape: 'CIRCLE',
            markerShapeOpen: null,
            markerOpacity: 1,
            lineWidth: 'THIN',
            lineWidthOverride: null,
            fillOpacity: 0.5,
            discreteColorPalette: 'BENCHLING',
            fillNullsWithDefaultValues: null,
            seriesColorConfigs: [],
          },
        },
        chartName: 'Box plot',
      };
    default:
      throw new Error(`Unsupported chart type: ${chartOptionType}`);
  }
}
